<template>
  <generic-modal :title="$t('appraisal')" ref="generic_modal" size="fullscreen">
    <form @submit.prevent="save">
      <!-- Main content with 3 columns layout -->
      <div class="columns is-gapless">
        <!-- Left Column - Basic Information -->
        <div class="column is-3">
          <div class="box m-1">
            <h5 class="title is-6 has-text-primary mb-4">
              <span class="icon"><i class="fa fa-home"></i></span>
              {{ $t("property details") }}
            </h5>

            <div class="field">
              <label class="label">{{ $t("property address") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="meta.property_address"
                  :placeholder="$t('property address')"
                  required
                />
              </div>
            </div>

            <div class="field">
              <generic-dropdown
                :title="$t('property type')"
                :placeholder="$t('select one')"
                :is_multiple="false"
                url="/appraisal/property-type/catalog"
                name_field="name"
                value_field="property_type_id"
                @selected="property_type_selected"
                ref="property_type_dropdown"
              />
            </div>

            <div class="field">
              <label class="label">{{ $t("valuation date") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="date"
                  v-model="meta.valuation_date"
                  required
                />
              </div>
            </div>

            <div class="field">
              <label class="label">{{ $t("status") }}</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="meta.status">
                    <option value="active">{{ $t("active") }}</option>
                    <option value="inactive">{{ $t("inactive") }}</option>
                    <option value="pending">{{ $t("pending") }}</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Valuation Section -->
            <h5 class="title is-6 has-text-success mt-5 mb-4">
              <span class="icon"><i class="fa fa-calculator"></i></span>
              {{ $t("valuation") }}
            </h5>

            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("price per sqm") }}</label>
                  <div class="control">
                    <input
                      class="input"
                      type="number"
                      step="0.01"
                      min="0"
                      v-model="meta.price_per_sqm"
                      :placeholder="$t('price per sqm')"
                      required
                      @input="calculate_total_value"
                    />
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("total area") }} (m²)</label>
                  <div class="control">
                    <input
                      class="input"
                      type="number"
                      step="0.01"
                      min="0"
                      v-model="meta.total_area"
                      :placeholder="$t('total area')"
                      required
                      @input="calculate_total_value"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Total Value Display -->
            <div class="field" v-if="calculated_total_value > 0">
              <label class="label">{{ $t("total value") }}</label>
              <div class="control">
                <input
                  class="input is-static has-text-weight-bold has-text-success"
                  type="text"
                  :value="format_currency(calculated_total_value)"
                  readonly
                />
              </div>
            </div>

            <!-- Notes Section -->
            <h5 class="title is-6 has-text-info mt-5 mb-4">
              <span class="icon"><i class="fa fa-sticky-note"></i></span>
              {{ $t("notes") }}
            </h5>

            <div class="field">
              <div class="control">
                <textarea
                  class="textarea"
                  v-model="meta.notes"
                  :placeholder="$t('notes')"
                  rows="4"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Center Column - Map and Location -->
        <div class="column is-6">
          <div class="box m-1" style="height: calc(100vh - 200px)">
            <h5 class="title is-6 has-text-warning mb-4">
              <span class="icon"><i class="fa fa-map-marker"></i></span>
              {{ $t("location") }}
            </h5>

            <!-- Location Search -->
            <div class="field">
              <label class="label">{{ $t("search location") }}</label>
              <div class="field has-addons">
                <div class="control has-icons-left is-expanded">
                  <input
                    class="input"
                    type="text"
                    v-model="search_query"
                    @keyup.enter="search_location()"
                    :placeholder="$t('enter address or place name')"
                  />
                  <span class="icon is-left">
                    <i class="fa fa-search"></i>
                  </span>
                </div>
                <div class="control">
                  <button
                    class="button is-primary"
                    @click="search_location()"
                    type="button"
                    :class="{ 'is-loading': searching_location }"
                    :disabled="!search_query || searching_location"
                  >
                    <span class="icon">
                      <i class="fa fa-search"></i>
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Map Container -->
            <div class="field">
              <div class="notification is-info is-light is-small">
                <p class="is-size-7 mb-2">
                  {{ $t("click on map to set location") }}
                </p>
                <div class="buttons are-small">
                  <button
                    class="button is-light"
                    @click="force_map_refresh()"
                    type="button"
                  >
                    <span class="icon"><i class="fa fa-refresh"></i></span>
                    <span>{{ $t("refresh map") }}</span>
                  </button>
                  <button
                    class="button is-info"
                    @click="switch_tile_provider()"
                    type="button"
                  >
                    <span class="icon"><i class="fa fa-globe"></i></span>
                    <span>{{ $t("switch tiles") }}</span>
                  </button>
                </div>
              </div>

              <div
                id="location-map"
                style="
                  height: calc(100vh - 380px);
                  width: 100%;
                  min-height: 400px;
                "
                ref="mapContainer"
              ></div>
            </div>

            <!-- Coordinates Input -->
            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("latitude") }}</label>
                  <div class="control">
                    <input
                      class="input is-small"
                      type="number"
                      step="0.000001"
                      min="-90"
                      max="90"
                      v-model="meta.latitude"
                      :placeholder="$t('latitude')"
                      @input="update_map_from_coordinates"
                    />
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">{{ $t("longitude") }}</label>
                  <div class="control">
                    <input
                      class="input is-small"
                      type="number"
                      step="0.000001"
                      min="-180"
                      max="180"
                      v-model="meta.longitude"
                      :placeholder="$t('longitude')"
                      @input="update_map_from_coordinates"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Photos -->
        <div class="column is-3">
          <div
            class="box m-1"
            style="height: calc(100vh - 200px); overflow-y: auto"
          >
            <h5 class="title is-6 has-text-danger mb-4">
              <span class="icon"><i class="fa fa-camera"></i></span>
              {{ $t("photos") }} ({{
                photos.length + selected_files.length
              }}/10)
            </h5>

            <!-- Upload Area -->
            <div
              class="field"
              v-if="photos.length + selected_files.length < 10"
            >
              <div class="file is-boxed is-centered">
                <label class="file-label">
                  <input
                    class="file-input"
                    type="file"
                    multiple
                    accept="image/*"
                    @change="handle_file_selection"
                    ref="file_input"
                  />
                  <span class="file-cta">
                    <span class="file-icon">
                      <i class="fa fa-upload"></i>
                    </span>
                    <span class="file-label">{{ $t("upload photos") }}</span>
                  </span>
                </label>
              </div>
            </div>

            <!-- Photo Grid -->
            <div class="columns is-multiline is-mobile">
              <!-- Selected Files Preview -->
              <div
                class="column is-6"
                v-for="(file, index) in selected_files"
                :key="'selected-' + index"
              >
                <div class="card">
                  <div class="card-image">
                    <figure class="image is-square">
                      <img
                        :src="file.preview"
                        alt="Preview"
                        style="object-fit: cover"
                      />
                    </figure>
                  </div>
                  <div class="card-content p-2">
                    <div class="field">
                      <div class="control">
                        <input
                          class="input is-small"
                          type="text"
                          v-model="file.description"
                          :placeholder="$t('photo description')"
                        />
                      </div>
                    </div>
                    <button
                      class="button is-small is-danger is-fullwidth"
                      type="button"
                      @click="remove_selected_file(index)"
                    >
                      <span class="icon is-small"
                        ><i class="fa fa-trash"></i
                      ></span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Saved Photos -->
              <div
                class="column is-6"
                v-for="photo in photos"
                :key="photo.uuid"
              >
                <div class="card">
                  <div class="card-image">
                    <figure class="image is-square">
                      <img
                        :src="get_photo_url(photo.file_path)"
                        alt="Property photo"
                        style="object-fit: cover"
                      />
                    </figure>
                  </div>
                  <div class="card-content p-2">
                    <div class="field">
                      <div class="control">
                        <input
                          class="input is-small"
                          type="text"
                          v-model="photo.description"
                          :placeholder="$t('photo description')"
                          @blur="update_photo_description(photo)"
                        />
                      </div>
                    </div>
                    <button
                      class="button is-small is-danger is-fullwidth"
                      type="button"
                      @click="delete_photo(photo)"
                      :disabled="saving"
                    >
                      <span class="icon is-small"
                        ><i class="fa fa-trash"></i
                      ></span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Add More Photos Card -->
              <div
                class="column is-6"
                v-if="
                  photos.length + selected_files.length > 0 &&
                  photos.length + selected_files.length < 10
                "
              >
                <div
                  class="card has-background-light"
                  style="
                    height: 200px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  "
                >
                  <div class="has-text-centered">
                    <label class="file-label">
                      <input
                        class="file-input"
                        type="file"
                        multiple
                        accept="image/*"
                        @change="handle_additional_files"
                        ref="additional_file_input"
                      />
                      <span class="file-cta is-large">
                        <span class="file-icon">
                          <i class="fa fa-plus fa-2x"></i>
                        </span>
                        <br />
                        <span class="file-label is-small">{{
                          $t("add photos")
                        }}</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Photo limit warning -->
            <div
              class="notification is-warning is-light is-small"
              v-if="photos.length + selected_files.length >= 8"
            >
              <p class="is-size-7">
                {{ $t("max photos exceeded") }} ({{
                  photos.length + selected_files.length
                }}/10)
              </p>
            </div>
          </div>
        </div>
      </div>
    </form>

    <!-- Error handling -->
    <div class="notification is-danger mt-3" v-if="error">
      <p v-html="error"></p>
    </div>

    <!-- Footer with v-slot:footer pattern -->
    <template v-slot:footer>
      <button
        class="button is-success"
        @click="save"
        :disabled="is_form_invalid"
        :class="{ 'is-loading': saving }"
      >
        <span class="icon"><i class="fa fa-save"></i></span>
        <span>{{ $t("save") }}</span>
      </button>
      <button
        class="button is-danger"
        @click="remove"
        v-if="meta.uuid"
        :disabled="saving"
      >
        <span class="icon"><i class="fa fa-trash"></i></span>
        <span>{{ $t("delete") }}</span>
      </button>
      <button class="button" @click="close_modal" :disabled="saving">
        <span class="icon"><i class="fa fa-close"></i></span>
        <span>{{ $t("cancel") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import {
  put,
  post,
  del,
  get,
  generate_error_message_text,
  confirm_deletion_dialog,
  success_message,
  error_message,
  warning_message,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";
import L from "leaflet";

export default {
  name: "AppraisalModal",
  components: {
    GenericModal,
    GenericDropdown,
  },
  data() {
    return {
      meta: {
        property_address: "",
        property_type_id: null,
        price_per_sqm: null,
        total_area: null,
        latitude: null,
        longitude: null,
        valuation_date: "",
        status: "active",
        notes: "",
        uuid: null,
      },
      photos: [],
      selected_files: [],
      show_map: false,
      error: "",
      saving: false,
      entity_uuid: "",
      map: null,
      marker: null,
      currentTileLayer: null,
      search_query: "",
      searching_location: false,
      search_timeout: null,
      updating_from_coordinates: false,
    };
  },
  computed: {
    is_form_invalid() {
      return (
        !this.meta.property_address ||
        !this.meta.property_type_id ||
        !this.meta.price_per_sqm ||
        !this.meta.total_area ||
        !this.meta.valuation_date ||
        this.meta.price_per_sqm <= 0 ||
        this.meta.total_area <= 0
      );
    },
    calculated_total_value() {
      if (this.meta.price_per_sqm && this.meta.total_area) {
        return (
          parseFloat(this.meta.price_per_sqm) * parseFloat(this.meta.total_area)
        );
      }
      return 0;
    },
  },
  watch: {
    search_query(newVal) {
      // Clear previous timeout
      if (this.search_timeout) {
        clearTimeout(this.search_timeout);
      }

      // Only search if there's meaningful input (at least 3 characters)
      if (newVal && newVal.length >= 3) {
        this.search_timeout = setTimeout(() => {
          this.search_location();
        }, 1000); // Wait 1 second after user stops typing
      }
    },
  },
  methods: {
    show_modal(uuid = null) {
      let v = this;
      v.entity_uuid = uuid;
      v.set_initial_state();

      if (uuid) {
        v.get_appraisal(uuid);
      } else {
        // Set default date to today
        v.meta.valuation_date = new Date().toISOString().split("T")[0];
      }

      v.$refs.generic_modal.show_modal();

      // Initialize map automatically when modal opens
      v.$nextTick(() => {
        setTimeout(() => {
          v.init_map();
        }, 300);
      });
    },

    toggle_map_visibility() {
      let v = this;
      v.show_map = !v.show_map;

      if (v.show_map) {
        // Initialize map when showing it
        v.$nextTick(() => {
          // Add a longer delay to ensure the DOM is fully rendered
          setTimeout(() => {
            v.init_map();

            // If editing an existing appraisal with coordinates, position map there
            if (v.meta.latitude && v.meta.longitude) {
              setTimeout(() => {
                if (v.map) {
                  v.map.setView(
                    [v.meta.latitude, v.meta.longitude],
                    v.map.getZoom()
                  );
                  v.add_marker(v.meta.latitude, v.meta.longitude);
                }
              }, 200);
            }
          }, 200);
        });
      } else {
        // Destroy map when hiding it
        v.destroy_map();
      }
    },

    close_modal() {
      this.destroy_map();
      this.$refs.generic_modal.close_modal();
      this.set_initial_state();
    },

    init_map() {
      let v = this;

      console.log("Initializing map...");
      console.log("mapContainer ref:", v.$refs.mapContainer);

      if (!v.$refs.mapContainer) {
        console.error("Map container not found!");
        return;
      }

      try {
        // Destroy existing map
        v.destroy_map();

        // Default coordinates (Guatemala City)
        let defaultLat = 14.6349;
        let defaultLng = -90.5069;
        let lat = v.meta.latitude || defaultLat;
        let lng = v.meta.longitude || defaultLng;

        console.log("Creating map at coordinates:", lat, lng);

        // Create new map
        v.map = L.map(v.$refs.mapContainer).setView([lat, lng], 13);

        console.log("Map created:", v.map);

        // Add tile layer with aggressive loading options
        v.currentTileLayer = L.tileLayer(
          "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          {
            attribution: "© OpenStreetMap contributors",
            maxZoom: 19,
            tileSize: 256,
            zoomOffset: 0,
            updateWhenIdle: false,
            updateWhenZooming: true,
            keepBuffer: 2,
            detectRetina: true,
            crossOrigin: true,
          }
        );

        v.currentTileLayer.addTo(v.map);

        // Force tile layer to load immediately
        v.currentTileLayer.on("loading", function () {
          console.log("Tiles loading...");
        });

        v.currentTileLayer.on("load", function () {
          console.log("Tiles loaded");
          v.map.invalidateSize();
        });

        // Multiple invalidateSize calls with different timings to ensure proper rendering
        setTimeout(() => {
          if (v.map) {
            v.map.invalidateSize();
            v.map._onResize(); // Force internal resize
          }
        }, 100);

        setTimeout(() => {
          if (v.map) {
            v.map.invalidateSize();
            v.map.redrawAll && v.map.redrawAll(); // Force redraw if available
          }
        }, 300);

        setTimeout(() => {
          if (v.map) {
            v.map.invalidateSize();
          }
        }, 500);

        // Add marker if coordinates exist
        if (v.meta.latitude && v.meta.longitude) {
          v.add_marker(v.meta.latitude, v.meta.longitude);
        }

        // Add click event to map
        v.map.on("click", function (e) {
          v.add_marker(e.latlng.lat, e.latlng.lng);
          v.meta.latitude = parseFloat(e.latlng.lat.toFixed(6));
          v.meta.longitude = parseFloat(e.latlng.lng.toFixed(6));
        });

        // Force a final refresh after everything is loaded
        setTimeout(() => {
          v.force_map_refresh();
        }, 800);
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    },

    add_marker(lat, lng) {
      let v = this;

      // Remove existing marker
      if (v.marker) {
        v.map.removeLayer(v.marker);
      }

      // Add new marker
      v.marker = L.marker([lat, lng], {
        draggable: true,
      }).addTo(v.map);

      // Add drag event to marker
      v.marker.on("dragend", function (e) {
        let position = e.target.getLatLng();
        v.meta.latitude = parseFloat(position.lat.toFixed(6));
        v.meta.longitude = parseFloat(position.lng.toFixed(6));
      });
    },

    force_map_refresh() {
      let v = this;
      if (v.map) {
        console.log("Forcing map refresh...");

        // 1. Multiple invalidate size calls
        v.map.invalidateSize();
        setTimeout(() => v.map.invalidateSize(), 50);
        setTimeout(() => v.map.invalidateSize(), 150);

        // 2. Force redraw of all layers
        v.map.eachLayer(function (layer) {
          if (layer.redraw) {
            layer.redraw();
          }
          // Force tile layer refresh
          if (layer._tiles) {
            Object.keys(layer._tiles).forEach((key) => {
              let tile = layer._tiles[key];
              if (tile.el) {
                tile.el.style.display = "none";
                setTimeout(() => {
                  tile.el.style.display = "block";
                }, 10);
              }
            });
          }
        });

        // 3. Aggressive pan sequence to force tile reload
        let center = v.map.getCenter();
        let zoom = v.map.getZoom();

        // Pan in different directions
        v.map.panTo([center.lat + 0.001, center.lng + 0.001]);
        setTimeout(() => {
          v.map.panTo([center.lat - 0.001, center.lng - 0.001]);
        }, 100);
        setTimeout(() => {
          v.map.panTo(center);
        }, 200);

        // 4. Zoom out and back in to force tile reload
        setTimeout(() => {
          v.map.setZoom(zoom - 1);
          setTimeout(() => {
            v.map.setZoom(zoom);
          }, 150);
        }, 300);

        // 5. Force container resize
        setTimeout(() => {
          let container = v.$refs.mapContainer;
          if (container) {
            container.style.height = "199px";
            setTimeout(() => {
              container.style.height = "200px";
              v.map.invalidateSize();
            }, 50);
          }
        }, 500);

        // 6. Final invalidation
        setTimeout(() => {
          v.map.invalidateSize();
          console.log("Map refresh completed");
        }, 800);
      }
    },

    switch_tile_provider() {
      let v = this;
      if (v.map && v.currentTileLayer) {
        // Remove current tile layer
        v.map.removeLayer(v.currentTileLayer);

        // Add alternative tile provider (CartoDB)
        v.currentTileLayer = L.tileLayer(
          "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
          {
            attribution: "© OpenStreetMap contributors © CARTO",
            maxZoom: 19,
            updateWhenIdle: false,
            updateWhenZooming: true,
            keepBuffer: 2,
          }
        );

        v.currentTileLayer.addTo(v.map);

        // Force refresh after switching
        setTimeout(() => {
          v.force_map_refresh();
        }, 200);
      }
    },

    search_location() {
      let v = this;

      if (!v.search_query.trim() || v.search_query.length < 3) {
        return;
      }

      v.searching_location = true;

      // Prepare search query with Guatemala context
      let query = v.search_query.trim();
      if (!query.toLowerCase().includes("guatemala")) {
        query += ", Guatemala";
      }

      // Use Nominatim geocoding service (OpenStreetMap's geocoding)
      const encodedQuery = encodeURIComponent(query);
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&limit=5&countrycodes=gt&addressdetails=1&extratags=1`;

      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          v.searching_location = false;

          if (data && data.length > 0) {
            // Get the best result (first one is usually the most relevant)
            const result = data[0];
            const lat = parseFloat(result.lat);
            const lng = parseFloat(result.lon);

            // Validate coordinates are within Guatemala bounds (approximately)
            if (lat >= 13.0 && lat <= 18.0 && lng >= -92.5 && lng <= -88.0) {
              // Set flag to prevent coordinate update loop
              v.updating_from_coordinates = true;

              // Update coordinates
              v.meta.latitude = parseFloat(lat.toFixed(6));
              v.meta.longitude = parseFloat(lng.toFixed(6));

              // Move map to location with appropriate zoom level
              if (v.map) {
                let zoomLevel = 15; // Default zoom

                // Adjust zoom based on place type
                if (result.type === "city" || result.type === "town") {
                  zoomLevel = 12;
                } else if (result.type === "village") {
                  zoomLevel = 14;
                } else if (
                  result.type === "house" ||
                  result.type === "building"
                ) {
                  zoomLevel = 18;
                }

                v.map.setView([lat, lng], zoomLevel);
                v.add_marker(lat, lng);
              }

              // Don't auto-update address unless it's empty to avoid overwriting user input
              if (!v.meta.property_address.trim()) {
                // Use a more readable address format
                let addressParts = [];
                if (result.address) {
                  if (result.address.house_number)
                    addressParts.push(result.address.house_number);
                  if (result.address.road)
                    addressParts.push(result.address.road);
                  if (result.address.neighbourhood)
                    addressParts.push(result.address.neighbourhood);
                  if (
                    result.address.city ||
                    result.address.town ||
                    result.address.village
                  ) {
                    addressParts.push(
                      result.address.city ||
                        result.address.town ||
                        result.address.village
                    );
                  }
                  if (result.address.state)
                    addressParts.push(result.address.state);
                }

                if (addressParts.length > 0) {
                  v.meta.property_address = addressParts.join(", ");
                } else {
                  v.meta.property_address = result.display_name;
                }
              }

              // Clear search query after successful search
              v.search_query = "";

              // Reset flag after a brief delay
              setTimeout(() => {
                v.updating_from_coordinates = false;
              }, 100);

              // Show success message
              success_message(v.$t("location found and set"));
            } else {
              warning_message(v.$t("location outside guatemala"));
            }
          } else {
            error_message(v.$t("location not found"));
          }
        })
        .catch((error) => {
          v.searching_location = false;
          console.error("Geocoding error:", error);
          error_message(v.$t("error searching location"));
        });
    },

    update_map_from_coordinates() {
      let v = this;

      if (v.updating_from_coordinates) return;

      // Only update map if it's visible and initialized
      if (v.show_map && v.meta.latitude && v.meta.longitude && v.map) {
        let lat = parseFloat(v.meta.latitude);
        let lng = parseFloat(v.meta.longitude);

        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          v.map.setView([lat, lng], v.map.getZoom());
          v.add_marker(lat, lng);
        }
      }
    },

    destroy_map() {
      if (this.map) {
        this.map.remove();
        this.map = null;
        this.marker = null;
      }
    },

    get_appraisal(uuid) {
      let v = this;
      get("/appraisal/appraisal", { uuid })
        .then((appraisal) => {
          v.meta = { ...v.meta, ...appraisal };

          // Set property type in dropdown
          v.$nextTick(() => {
            if (appraisal.property_type_id) {
              v.$refs.property_type_dropdown.set_value(
                appraisal.property_type_id
              );
            }

            // Don't automatically initialize map, wait for user to click show map
            // If user wants to see/edit location, they can click the show map button
          });

          // Load photos
          v.load_photos();
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        });
    },

    load_photos() {
      let v = this;
      if (!v.meta.uuid) return;

      get("/appraisal/photo", { appraisal_uuid: v.meta.uuid })
        .then((photos) => {
          v.photos = photos;
        })
        .catch((error) => {
          console.error("Error loading photos:", error);
        });
    },

    property_type_selected(propertyType) {
      if (propertyType) {
        this.meta.property_type_id = propertyType.property_type_id;
      }
    },

    handle_file_selection(event) {
      let v = this;
      let files = Array.from(event.target.files);

      if (files.length + v.selected_files.length > 10) {
        v.error = v.$t("max photos exceeded");
        return;
      }

      files.forEach((file) => {
        // Validar tamaño de archivo (5MB)
        if (file.size > 5 * 1024 * 1024) {
          v.error = v.$t("file too large");
          return;
        }

        // Validar tipo de archivo
        if (!file.type.startsWith("image/")) {
          v.error = v.$t("invalid file type");
          return;
        }

        // Crear preview
        let reader = new FileReader();
        reader.onload = function (e) {
          v.selected_files.push({
            file: file,
            preview: e.target.result,
            description: "",
          });
        };
        reader.readAsDataURL(file);
      });

      // Limpiar input
      event.target.value = "";
    },

    handle_additional_files(event) {
      this.handle_file_selection(event);
    },

    remove_selected_file(index) {
      this.selected_files.splice(index, 1);
    },

    get_photo_url(file_path) {
      // Asumiendo que las fotos se sirven desde el servidor
      return file_path;
    },

    delete_photo(photo) {
      let v = this;
      confirm_deletion_dialog(v.$t("are you sure delete photo"), v, () => {
        del("/appraisal/photo", { uuid: photo.uuid })
          .then(() => {
            success_message(v.$t("photo deleted successfully"));
            v.load_photos();
          })
          .catch((error) => {
            v.error = generate_error_message_text(error);
          });
      });
    },

    update_photo_description(photo) {
      post("/appraisal/photo/update", {
        uuid: photo.uuid,
        description: photo.description,
      }).catch((error) => {
        console.error("Error updating photo description:", error);
      });
    },

    upload_photos() {
      let v = this;
      if (v.selected_files.length === 0 || !v.meta.uuid)
        return Promise.resolve();

      let formData = new FormData();
      formData.append("appraisal_uuid", v.meta.uuid);

      let descriptions = [];
      v.selected_files.forEach((fileObj, index) => {
        formData.append("photos", fileObj.file);
        descriptions.push(fileObj.description);
      });

      formData.append("descriptions", JSON.stringify(descriptions));

      return fetch("/appraisal/photo/upload", {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`, // Ajustar según tu sistema de auth
        },
      })
        .then((response) => response.json())
        .then(() => {
          v.selected_files = [];
          v.load_photos();
        });
    },

    save() {
      let v = this;
      if (v.is_form_invalid) return;

      v.saving = true;
      v.error = "";

      const isEdit = !!v.entity_uuid;
      const method = isEdit ? post : put;
      const endpoint = "/appraisal/appraisal";

      method(endpoint, v.meta)
        .then((response) => {
          if (!isEdit && response && response.uuid) {
            v.meta.uuid = response.uuid;
          }

          // Upload photos if any are selected
          return v.upload_photos();
        })
        .then(() => {
          success_message(v.$t("appraisal saved successfully"));
          v.$emit("success");
          v.close_modal();
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        })
        .finally(() => {
          v.saving = false;
        });
    },

    remove() {
      let v = this;
      confirm_deletion_dialog(v.$t("are you sure delete appraisal"), v, () => {
        del("/appraisal/appraisal", { uuid: v.meta.uuid })
          .then(() => {
            success_message(v.$t("appraisal deleted successfully"));
            v.$emit("success");
            v.close_modal();
          })
          .catch((error) => {
            v.error = generate_error_message_text(error);
          });
      });
    },

    set_initial_state() {
      this.meta = {
        property_address: "",
        property_type_id: null,
        price_per_sqm: null,
        total_area: null,
        latitude: null,
        longitude: null,
        valuation_date: "",
        status: "active",
        notes: "",
        uuid: null,
      };
      this.photos = [];
      this.selected_files = [];
      this.show_map = false;
      this.error = "";
      this.saving = false;
      this.entity_uuid = "";
      this.search_query = "";
      this.searching_location = false;

      // Clear dropdown
      if (this.$refs.property_type_dropdown) {
        this.$refs.property_type_dropdown.clear();
      }
    },

    calculate_total_value() {
      if (this.meta.price_per_sqm && this.meta.total_area) {
        return (
          parseFloat(this.meta.price_per_sqm) * parseFloat(this.meta.total_area)
        );
      }
      return 0;
    },

    format_currency(value) {
      if (!value) return "Q. 0.00";
      return (
        "Q. " +
        parseFloat(value).toLocaleString("es-GT", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
  },

  beforeDestroy() {
    this.destroy_map();
    // Clear any pending search timeout
    if (this.search_timeout) {
      clearTimeout(this.search_timeout);
    }
  },
};
</script>

<style scoped>
@import "leaflet/dist/leaflet.css";

#location-map {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  z-index: 1;
  min-height: 400px;
  height: calc(100vh - 380px);
  width: 100%;
  position: relative;
  overflow: hidden;
}

#location-map .leaflet-container {
  height: 100% !important;
  width: 100% !important;
  z-index: 1;
}

#location-map .leaflet-tile-pane {
  z-index: 1;
}

#location-map .leaflet-tile {
  max-width: none !important;
  max-height: none !important;
}

/* Ensure tiles load properly */
#location-map .leaflet-layer {
  opacity: 1 !important;
}

/* Fix for modal z-index issues */
#location-map .leaflet-control-container {
  z-index: 2;
}

.file-cta.is-large {
  padding: 2rem;
  flex-direction: column;
}

.file-cta.is-large .file-icon {
  margin-bottom: 0.5rem;
}

.card-image .image {
  overflow: hidden;
}

.card-image .image img {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.notification.is-small {
  padding: 0.5rem 1rem;
  margin-bottom: 0.5rem;
}

.file-input {
  opacity: 0;
  position: absolute;
  z-index: -1;
}

.card {
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
</style>
